import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Divider, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  weekdaysHelper,
  shiftHelper,
  hoursHelper,
  datesHelper,
} from "../../helpers/courses";

const useStyles = (theme, isMobile) => ({
  container: {
    width: {
      xl: "1309px",
      lg: "1034px",
      md: "814.4px",
      sm: "220px",
      xs: "220px",
    },
    height: isMobile ? "349px" : "119px",
    mb: { xs: "21px", md: "61px" },
    padding: "20px 32px",
    objectFit: "contain",
    borderRadius: "47px",
    boxShadow: "0 4px 93px 0 rgba(0, 31, 85, 0.1)",
    border: "solid 5px #fff",
    backgroundColor: "#f5f5f5",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "space-evenly",
    alignItems: "center",
  },
  child: {
    display: "flex",
    alignItems: { xs: "center", md: "baseline" },
    flexDirection: { xs: "column" },
    width: { md: "290px", lg: "432px" },
  },
  divider: {
    height: isMobile ? "1px" : "70%",
    width: isMobile ? "70%" : "0.5px",
    mx: "16px",
    backgroundColor: theme.palette.blue.main,
  },
  shift: {
    width: { xl: "485px", lg: "450px", md: "350px", sm: "235px", xs: "235px" },
    height: "65px",
    flexGrow: "0",
    borderRadius: "32.5px",
    border: "solid 2px #f4f4f4",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  applyButton: {
    width: {
      xl: "223.2px",
      lg: "180px",
      md: "210px",
      sm: "223.3px",
      xs: "223.3px",
    },
    height: { xl: "64px", lg: "60px", md: "45px" },
    borderRadius: "100px",
    background: "linear-gradient(101deg, #f32c4d, #d00e2e 100%)",
    color: theme.palette.white.main,
    textTransform: "none",
    fontSize: { xl: "15.5px", md: "13px" },
    fontWeight: 550,
    margin: {
      xl: "10px 0px 10px",
      lg: "10px 0px 10px",
      md: "10px 0px 10px",
      sm: "0",
    },
  },
  buttonIcon: {
    p: "5px",
    height: "14px",
    width: "14px",
    borderRadius: "58px",
    color: "#d00e2e",
    bgcolor: theme.palette.white.main,
    ml: "3px",
    boxShadow:
      "4px 8px 20px 0 rgba(0, 0, 0, 0.15), inset 7px 0 20px 0 rgba(255, 255, 255, 0.15)",
  },
  icon: {
    width: isMobile ? "40.5px" : { xl: "58px", lg: "58px", md: "40px" },
    height: isMobile ? "39.1px" : { xl: "56px", lg: "56px", md: "38px" },
    borderRadius: "50%",
    border: "solid 2px #f4f4f4",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "5px",
  },
  typography: {
    subTitle: {
      color: theme.palette.red.main,
      fontFamily: theme.typography.anaHeim.regular,
      fontSize: { xl: "24px", lg: "20px", md: "15px" },
    },
    title: {
      color: theme.palette.blue.main,
      fontFamily: theme.typography.montserrat.bold,
      fontSize: { xl: "28px", lg: "28px", md: "18px" },
      textTransform: "uppercase",
      mr: { md: "5px" },
      whiteSpace: "nowrap",
    },
    shift: {
      color: theme.palette.blue.main,
      fontFamily: theme.typography.montserrat.regular,
      fontSize: { xl: "22px", lg: "20px", md: "16px" },
      marginLeft: "5px",
    },
    dates: {
      color: theme.palette.blue.main,
      fontFamily: theme.typography.montserrat.light,
      fontSize: { xl: "25px", lg: "20px", md: "16px" },
      whiteSpace: "nowrap",
    },
  },
  box: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { md: "center" },
    textAlign: "center",
  },
});

const ApplyDates = ({ course, pathname }) => {
  const isMobile = useMediaQuery("(max-width:899px)");

  const theme = useTheme();
  const classes = useStyles(theme, isMobile);

  const {
    weekDays,
    month,
    year,
    startDate,
    endDate,
    shift,
    startHour,
    endHour,
    cohortLabel,
    mode,
    type,
    priceARS,
    priceUSD,
  } = course;

  const navigate = useNavigate();

  const data = {
    cohortLabel,
    startDate,
    endDate,
    startHour,
    endHour,
    shift,
    priceARS,
    priceUSD,
    month,
    mode,
    weekDays,
    type,
  };

  const handleApply = () => {
    navigate(pathname);
    localStorage.setItem("data", JSON.stringify(data));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.child}>
        <Typography sx={classes.typography.subTitle}>
          {weekdaysHelper(weekDays)}
        </Typography>
        <Box sx={classes.box}>
          <Typography sx={classes.typography.title}>
            {month} {year} {isMobile ? null : "|"}
          </Typography>

          <Typography sx={classes.typography.dates}>
            {datesHelper(startDate)[0]}/{datesHelper(startDate)[1]} AL{" "}
            {datesHelper(endDate)[0]}/{datesHelper(endDate)[1]}
          </Typography>
        </Box>
      </Box>
      <Divider
        orientation={isMobile ? "horizontal" : "vertical"}
        sx={classes.divider}
      />
      <Box sx={classes.shift}>
        <Box sx={classes.icon}>
          <img
            src={shiftHelper(shift) && shiftHelper(shift).icon}
            alt="shift"
          />
        </Box>
        {isMobile ? (
          <Box>
            <Typography sx={classes.typography.shift}>
              <b>{shiftHelper(shift) && shiftHelper(shift).shift}</b>
            </Typography>
            <Typography sx={classes.typography.shift}>
              {hoursHelper(startHour)} A {hoursHelper(endHour)}HS.
            </Typography>
          </Box>
        ) : (
          <Typography sx={classes.typography.shift}>
            <b>{shiftHelper(shift) && shiftHelper(shift).shift}</b>{" "}
            {isMobile ? "" : "|"} {hoursHelper(startHour)} A{" "}
            {hoursHelper(endHour)}HS.
          </Typography>
        )}
      </Box>
      <Divider
        orientation={isMobile ? "horizontal" : "vertical"}
        sx={classes.divider}
      />
      <Box>
        <Button
          sx={classes.applyButton}
          endIcon={<ArrowForwardIosIcon sx={classes.buttonIcon} />}
          onClick={handleApply}
        >
          Aplicar
        </Button>
      </Box>
    </Box>
  );
};

export default ApplyDates;
